import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "tss-react/mui";
import Backdrop from "@mui/material/Backdrop";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";

import { ButtonUI, KMKDialog } from "../../Common";
import { showToast } from "../../../store/actions";
import AddOIDC from "./AddOIDC";
import Mapping from "./Mapping";
import UpdateClientIDClientSecret from "./UpdateClientIDClientSecret";
import AuthProviderService from "../../../service/authProvider";

const useStyles = makeStyles()((theme) => ({
  Dialog: {
    margin: 5,
    "& .MuiDialog-paper": {
      maxWidth: "760px",
    },
  },
  Toolbar: {
    fontSize: "1.1rem",
    backgroundColor: "white",
    color: "black",
  },
  iconContainer: {
    "& .MuiStepConnector-root": {
      width: "13.73px",
      maxWidth: "13.73px",
    },
  },
  container: {
    padding: 24,
    backgroundColor: "#FFFFFF",
  },
  [theme.breakpoints.down("510")]: {
    iconContainer: {
      display: "flex",
      flexWrap: "wrap",
      marginLeft: "10px",
    },
  },
}));

export default function AddForm({ setOpen, IDP_Id, loadIDPData }) {
  const [idpId, setIdpId] = useState(0);
  const [idpName, setIdpName] = useState("");
  const [MetaDataURL, setMetaDataURL] = useState("");
  const [claimLoginId, setClaimLoginId] = useState("");
  const [claimEmail, setClaimEmail] = useState("");
  const [claimFirstname, setClaimFirstname] = useState("");
  const [claimLastname, setClaimLastname] = useState("");
  const [claimValues, setClaimValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enable, setEnable] = useState(0);
  const [clientID, setClientID] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [OIDCData, setOIDCData] = useState([]);
  const [activeStep, setActiveStep] = useState(0);

  const { classes } = useStyles();
  const dispatch = useDispatch();

  const fieldObject1 = {
    idpName,
    setIdpName,
    MetaDataURL,
    setMetaDataURL,
  };

  const fieldObject2 = {
    claimLoginId,
    setClaimLoginId,
    claimEmail,
    setClaimEmail,
    claimFirstname,
    setClaimFirstname,
    claimLastname,
    setClaimLastname,
  };

  const fieldObject3 = {
    clientID,
    setClientID,
    clientSecret,
    setClientSecret,
    enable,
    setEnable,
  };

  const MainTitle = IDP_Id > 0 ? "Edit OIDC Identity Provider" : "Add OIDC Identity Provider";
  const succMsg = IDP_Id > 0 ? "Identity Provider Updated Successfully!" : "Identity Provider Added Successfully!";
  const Title1 = idpId > 0 ? "Edit Name and Metadata" : "Add Name and Metadata";
  const Title3 = IDP_Id > 0 ? "Edit Configuration Details" : "Add Configuration Details";
  const steps = [Title1, "Configure Attribute Mapping", Title3];

  const handleNext = async () => {
    setLoading(true);

    const authProviderService = new AuthProviderService();
    if (activeStep === 0) {
      const IDPData = {
        name: idpName,
        metaDataURL: MetaDataURL,
      };
      let response = null;
      if (idpId > 0) response = await authProviderService.updateOIDCIDPMetadata(idpId, IDPData);
      else response = await authProviderService.createOIDCIDP(IDPData);

      if (response.success) {
        if (idpId === 0) setIdpId(response.data.IDP_id);

        setClaimValues(response.data.ConfigMapping);
        const data = {
          GrantType: response.data.GrantType,
          CallbackURL: response.data.CallbackURL,
          LogoutURL: response.data.LogoutURL,
        };
        setOIDCData(data);
        loadIDPData();
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      } else {
        setClaimValues([]);
      }
    } else if (activeStep === 1) {
      const data = {
        loginId: claimLoginId,
        email: claimEmail,
        firstName: claimFirstname,
        lastName: claimLastname,
      };

      const response = await authProviderService.updateOIDCIDP(idpId, data);

      if (response.success) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    } else {
      const response = await authProviderService.updateOIDCIDP(idpId, {
        client_id: clientID,
        client_secret: clientSecret,
        status: enable ? 1 : 0,
      });

      if (response.success) {
        clearFormData();
        loadIDPData();
        dispatch(
          showToast({
            message: succMsg,
            type: "success",
          })
        );

        setOpen(false);
        setActiveStep(0);
      }
    }
    setLoading(false);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  useEffect(() => {
    if (IDP_Id > 0) {
      setIdpId(IDP_Id);

      const getIDP = async () => {
        setLoading(true);

        const authProviderService = new AuthProviderService();
        const response = await authProviderService.getIDP(IDP_Id);

        if (response.success) {
          const IDPData = response.data;
          const ConfigMapping = response.data.Mapping;

          setIdpName(IDPData.Name);
          setMetaDataURL(IDPData.MetaDataURL);
          setClaimLoginId(ConfigMapping.LoginId);
          setClaimEmail(ConfigMapping.Email);
          setClaimFirstname(ConfigMapping.FirstName);
          setClaimLastname(ConfigMapping.LastName);
          setClientID(IDPData.Client.Id);
          setClientSecret(IDPData.Client.Secret);
          setEnable(IDPData.Status);
        }

        setLoading(false);
      };

      getIDP();
    }
  }, [IDP_Id]);

  const handleCancel = () => {
    setOpen(false);
  };

  const clearFormData = () => {
    setIdpName("");
    setMetaDataURL("");
    setClaimLoginId("");
    setClaimEmail("");
    setClaimFirstname("");
    setClaimLastname("");
    setEnable(0);
    setClientID("");
    setClientSecret("");
  };

  return (
    <KMKDialog open={true} maxWidth="md" className={classes.Dialog}>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar className={classes.Toolbar}>{MainTitle}</Toolbar>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "black",
          }}
        >
          <CloseIcon />
        </IconButton>
      </AppBar>
      <Box sx={{ width: "100%" }} className={classes.container}>
        <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>

        <Stepper activeStep={activeStep} className={classes.iconContainer}>
          {steps.map((label) => {
            const stepProps = {};
            const labelProps = {};

            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>

        <React.Fragment>
          {activeStep === 0 ? (
            <AddOIDC {...fieldObject1} />
          ) : activeStep === 1 ? (
            <Mapping {...fieldObject2} claimValues={claimValues} />
          ) : (
            <UpdateClientIDClientSecret {...fieldObject3} OIDCData={OIDCData} />
          )}
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            <Box sx={{ flex: "1 1 auto" }} />

            <ButtonUI
              variant="contained"
              color="secondary"
              onClick={activeStep === 0 ? handleCancel : handleBack}
              sx={{ mr: 1 }}
            >
              {activeStep === 0 ? "Cancel" : "Back"}
            </ButtonUI>

            <ButtonUI
              variant="contained"
              color="success"
              onClick={handleNext}
              disabled={
                (activeStep === 0 && !(idpName.trim() && MetaDataURL.trim())) ||
                (activeStep === 1 && !(claimLoginId.trim() && claimEmail && claimFirstname && claimLastname)) ||
                (activeStep === 2 && !(clientID.trim() && clientSecret.trim())) ||
                loading
              }
            >
              {activeStep === steps.length - 1 ? "Finish" : "Save and Continue"}
            </ButtonUI>
          </Box>
        </React.Fragment>
      </Box>
    </KMKDialog>
  );
}
